import React from 'react';
import { Alert, AlertTitle, Button, IconButton, Stack, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getNextLicenseTier } from '@responsedock/config';
import type { BillingTier } from '../../lib/billing';
import { formatCount, planFor } from '../../lib/planCatalog';
import { responsesQuotaState, useWorkspaceUsage, type WorkspaceUsage } from '../../hooks/useWorkspaceUsage';
import { usePlanGateStore } from '../../store/planGate';
import { useAuthStore } from '../../store/auth';
import { wrappingAlertSx } from '../../lib/alertLayout';

function formatDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

export interface QuotaCopy {
  state: 'warning' | 'exceeded';
  title: string;
  body: string;
  upgradeTier: BillingTier | null;
  actionLabel: string;
}

/**
 * "Your surveys stopped collecting responses: 500/500 Starter responses used this month. Upgrade to
 * Pro (10,000/month) to resume now, or collection restarts on November 1." Numbers come from the
 * usage API and the plan config.
 */
export function quotaCopy(usage: WorkspaceUsage | null | undefined): QuotaCopy | null {
  const state = responsesQuotaState(usage);
  if (!usage || state === 'ok') return null;
  const tier = usage.tier;
  const plan = planFor(tier);
  const nextTier = getNextLicenseTier(tier) as BillingTier | null;
  const nextPlan = nextTier ? planFor(nextTier) : null;
  const used = formatCount(usage.responsesUsed);
  const limit = formatCount(usage.responsesLimit);
  const restart = formatDay(usage.periodEnd);
  const nextVolume = nextPlan?.quotas.responsesPerMonth;
  const upgradeText = nextPlan
    ? nextVolume == null
      ? `Talk to us about ${nextPlan.name} for custom volume`
      : `Upgrade to ${nextPlan.name} (${formatCount(nextVolume)}/month)`
    : 'Contact us for more volume';
  const actionLabel = nextPlan ? (nextTier === 'ENTERPRISE' ? 'Talk to sales' : `Upgrade to ${nextPlan.name}`) : 'Contact us';

  if (state === 'exceeded') {
    return {
      state,
      title: 'Your surveys stopped collecting responses',
      body: `${used}/${limit} ${plan.name} responses used this month. ${upgradeText} to resume now${restart ? `, or collection restarts on ${restart}` : ''}.`,
      upgradeTier: nextTier,
      actionLabel,
    };
  }
  return {
    state,
    title: `${used}/${limit} ${plan.name} responses used this month`,
    body: `At ${limit} your surveys stop collecting responses${restart ? ` until ${restart}` : ''}. ${upgradeText} to keep collecting.`,
    upgradeTier: nextTier,
    actionLabel,
  };
}

const DISMISS_KEY = 'ResponseDock-quota-warning-dismissed:';

/** Top-of-page banner when the monthly response quota is nearly or fully used. */
export function QuotaBanner({ canManageBilling = true }: { canManageBilling?: boolean }) {
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const usage = useWorkspaceUsage().data;
  const openPlanPicker = usePlanGateStore((s) => s.openPlanPicker);
  const copy = quotaCopy(usage);
  const dismissKey = `${DISMISS_KEY}${usage?.periodEnd ?? ''}`;
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    try {
      setDismissed(window.sessionStorage.getItem(dismissKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  if (isEmbedded || !copy) return null;
  if (copy.state === 'warning' && dismissed) return null;

  const action = canManageBilling ? (
    <Button
      color="inherit"
      size="small"
      variant="outlined"
      sx={{ whiteSpace: 'nowrap' }}
      onClick={() => {
        if (copy.upgradeTier === 'ENTERPRISE' || !copy.upgradeTier) {
          window.location.href = 'mailto:sales@responsedock.com';
          return;
        }
        openPlanPicker({ highlightTier: copy.upgradeTier });
      }}
    >
      {copy.actionLabel}
    </Button>
  ) : null;

  return (
    <Alert
      severity={copy.state === 'exceeded' ? 'error' : 'warning'}
      data-testid="quota-banner"
      data-state={copy.state}
      sx={{ borderRadius: 0, ...wrappingAlertSx }}
      action={(
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {action}
          {copy.state === 'warning' ? (
            <Tooltip title="Hide until next session">
              <IconButton
                size="small"
                color="inherit"
                aria-label="Dismiss response quota warning"
                onClick={() => {
                  try { window.sessionStorage.setItem(dismissKey, '1'); } catch { /* view-only dismiss */ }
                  setDismissed(true);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      )}
    >
      <AlertTitle sx={{ mb: 0.25 }}>{copy.title}{copy.state === 'exceeded' ? ': ' : ' '}</AlertTitle>
      {copy.body}
      {!canManageBilling ? ' Ask a workspace owner or admin to upgrade.' : ''}
    </Alert>
  );
}
